<template>
  <div class="id-scope private-booking-roster scard" style="padding:16px">
    <!-- date band (browsing date comes from the PARENT page's own calendar — read-only here) -->
    <div class="roster-head">
      <div class="col" style="gap:2px">
        <h3 class="t-h3">{{ $t('privateBooking.title') }} {{ $formatFullDate(bookingDate) }}</h3>
        <span class="t-cap">{{ bookingDate.toLocaleDateString('en-GB') }}</span>
      </div>
    </div>

    <!-- loading skeleton -->
    <div v-if="loadingPrivateBooking" class="col priv-list" style="gap:8px">
      <div v-for="i in 3" :key="i" class="id-skel" style="height:52px"></div>
    </div>

    <!-- empty -->
    <div v-else-if="!privateBookingList.length" class="id-empty">
      <div class="mdi mdi-account-heart-outline"></div>
      <div style="margin-top:8px">{{ $t('privateBooking.noBooking') }}</div>
    </div>

    <!-- flat list — one row per private session (no time-slot column grouping: private
         sessions are ad-hoc, they don't share a recurring slot the way group classes do).
         Card style matches the coach-schedule Private section (GroupingSheet.vue's
         .gs-pv-* block) — stacked icon-rows instead of the roster pill/avatar layout. -->
    <div v-else class="col priv-list id-fade-in" style="gap:8px" :key="bookingDate.getTime()">
      <div v-for="booking in privateBookingList" :key="booking.privatebookingid"
        class="priv-row" :style="{ cursor: showActions ? 'pointer' : 'default' }"
        @click="onRowClick($event, booking)">
        <div class="priv-line1">
          <span class="priv-time"><span class="mdi mdi-clock-outline"></span> {{ booking.classtime }}</span>
          <span class="badge badge-info">{{ privateTypeLabel(booking.privatetype) }}</span>
          <span class="badge badge-neutral">{{ $t('grouping.privateHours', { n: booking.hours }) }}</span>
          <span v-if="booking.checkedin == 1" class="badge badge-success">{{ $t('bookingList.menuCheckedInBadge') }}</span>
        </div>
        <div class="priv-coach"><span class="mdi mdi-whistle-outline"></span> {{ coachDisplayName(booking) }}</div>
        <!-- price — only rendered in staff view (Dashboard passes showPrice; the backend
             already omits the price field entirely for coach/customer callers) -->
        <div v-if="showPrice && booking.price !== undefined && booking.price !== null" class="priv-money">
          <span class="mdi mdi-cash"></span> ฿{{ $money(booking.price) }}
          <span class="badge" :class="Number(booking.paid) === 1 ? 'badge-success' : 'badge-warning'">
            {{ Number(booking.paid) === 1 ? $t('customerCourse.paid') : $t('customerCourse.unpaid') }}
          </span>
        </div>
        <div class="priv-students"><span class="mdi mdi-account-multiple-outline"></span> {{ studentNamesText(booking) }}</div>
        <div v-if="booking.note" class="priv-note">{{ booking.note }}</div>
      </div>
    </div>

    <!-- quick action menu (admin/Dashboard only): check-in only, same as the group-class
         roster (BookingListAdmin.vue) — editing/cancelling a Private booking now lives in
         its own management page, not this quick roster view.
         min-width is explicit because :target is the full-width row: Vuetify's connected
         location strategy defaults the popup's min-width to the target's width otherwise,
         which stretched this menu edge-to-edge across the card. -->
    <v-menu v-if="showActions" v-model="menuOpen" :target="menuTarget" location="bottom start" offset="8"
      min-width="200" max-width="260"
      :close-on-content-click="false" :persistent="true" transition="scale-transition" origin="top left">
      <div class="popmenu quick-menu">
        <div class="popmenu-head">
          <span class="popmenu-name">{{ selectedBooking ? (selectedBooking.classtime + ' · ' + coachDisplayName(selectedBooking)) : '—' }}</span>
          <span v-if="isCheckedIn" class="badge badge-success" style="height:20px;font-size:11px">
            <v-icon size="12">mdi-check-decagram</v-icon>{{ $t('bookingList.menuCheckedInBadge') }}
          </span>
        </div>
        <button class="popmenu-item" @click="onChooseCheckin">
          <span class="pm-ico"><v-icon size="18" :color="isCheckedIn ? '#b45309' : '#047857'">{{ isCheckedIn ? 'mdi-undo-variant' : 'mdi-check-bold' }}</v-icon></span>
          {{ isCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin') }}
        </button>
      </div>
    </v-menu>

    <!-- check-in / undo check-in confirmation -->
    <id-modal v-model="checkinConfirmDialog" size="sm" persistent
      :icon="isCheckedIn ? 'mdi-undo-variant' : 'mdi-check-circle'"
      :title="isCheckedIn ? $t('bookingList.menuUndoCheckin') : $t('bookingList.menuCheckin')">
      <p style="margin:0">{{ isCheckedIn
        ? $t('bookingMgmt.confirmUndoCheckin', { name: selectedBooking ? studentNamesText(selectedBooking) : '' })
        : $t('bookingMgmt.confirmCheckin', { name: selectedBooking ? studentNamesText(selectedBooking) : '' }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="checkinConfirmDialog = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmCheckin"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex';
import { togglePrivateBookingCheckin } from '@/utils/privateBookingCheckin';

export default {
  name: 'PrivateBookingList',
  props: {
    // the PARENT page's own browsing date (ViewClasses' `datepick` / Dashboard's `datepick`) —
    // used purely to fetch/display; never fed into the (fully independent) add-booking modal.
    bookingDate: {
      type: Date,
      required: true,
    },
    // staff view (Dashboard) — show the price column. The backend already omits `price`
    // entirely for coach/customer callers, so this is mostly a defensive extra guard.
    showPrice: {
      type: Boolean,
      default: false,
    },
    // staff view (Dashboard) — show the per-row check-in quick-action menu.
    showActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      privateBookingList: [],
      loadingPrivateBooking: false,
      interval: null,
      menuOpen: false,
      menuTarget: null,
      selectedBooking: null,
      checkinConfirmDialog: false,
    }
  },
  created() {
    this.getPrivateBookingList()
  },
  mounted() {
    if (this.showActions) {
      document.addEventListener('mousedown', this.onDocumentMousedown, true);
      document.addEventListener('keydown', this.onDocumentKeydown);
    }
    // keep the list fresh the same way Dashboard/BookingManagement self-refresh
    this.interval = setInterval(() => {
      this.getPrivateBookingList(true) // silent background poll — no skeleton flicker, no error dialogs
    }, 60000)
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    clearInterval(this.interval)
  },
  watch: {
    // parent's own browsing calendar changed — re-fetch for the new date (browse-only,
    // this never feeds the independent add-booking modal's own date field)
    bookingDate() {
      this.getPrivateBookingList()
    },
  },
  computed: {
    ...mapGetters({
      token: 'getToken',
    }),
    isCheckedIn() {
      const b = this.selectedBooking;
      if (!b) return false;
      return parseInt(b.checkedin) === 1;
    },
  },
  methods: {
    SQLDate(date) {
      return moment(date).format('YYYY-MM-DD')
    },
    // silent=true for the 60s background poll — a transient/stale failure must not wipe the list
    // or pop the App-level error dialog (only 401 still forces re-login)
    async getPrivateBookingList(silent = false) {
      if (!silent) this.loadingPrivateBooking = true
      const bookingdate = this.SQLDate(this.bookingDate)
      try {
        const token = this.$store.getters.getToken;
        const response = await axios.post(this.baseURL + '/getPrivateBookingList', { bookingdate }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // date changed again while this request was in flight — drop the now-stale response
        if (bookingdate !== this.SQLDate(this.bookingDate)) return;
        if (response.data.success) {
          this.privateBookingList = response.data.bookinglist || []
        } else if (!silent) {
          this.privateBookingList = []
          this.$emit('onErrorHandler', response.data.message || this.$t('msg.loadFail'))
        }
      } catch (error) {
        // stale rejection after a date switch — ignore (a newer request is in flight)
        if (bookingdate !== this.SQLDate(this.bookingDate)) return;
        if (error.response && error.response.status === 401) {
          this.privateBookingList = []
          this.$emit('onErrorHandler', error.response.data.message)
          this.$emit('onClickChangeState', 'login')
        } else if (!silent) {
          this.privateBookingList = []
          this.$emit('onErrorHandler', error.message)
        }
      } finally {
        if (!silent) this.loadingPrivateBooking = false
      }
    },
    // coach display name — tolerate a raw username fallback if the joined name is missing
    coachDisplayName(booking) {
      return (booking.coachname && booking.coachname.trim()) || booking.coachusername || '';
    },
    privateTypeLabel(privatetype) {
      const map = {
        '1on1': 'customerCourse.private1on1',
        '2on1': 'customerCourse.private2on1',
        '3on1': 'customerCourse.private3on1',
      };
      return map[privatetype] ? this.$t(map[privatetype]) : privatetype;
    },
    // student name(s) sharing the session (1-3). Other families' children in the same
    // 2on1/3on1 session are masked by the backend as a count (othersCount), not a name.
    studentNamesText(booking) {
      const names = (booking.students || []).map(s => s.nickname || s.fullname || s.name).filter(Boolean);
      let text = names.join(', ');
      if (booking.othersCount) {
        const othersText = this.$t('privateBooking.othersCount', { n: booking.othersCount });
        text = text ? `${text} + ${othersText}` : othersText;
      }
      return text || '-';
    },
    onRowClick(event, booking) {
      if (!this.showActions) return;
      this.selectedBooking = booking;
      this.menuTarget = event.currentTarget;
      this.menuOpen = true;
    },
    onDocumentMousedown(event) {
      if (!this.menuOpen) return;
      const t = event.target;
      if (!t) return;
      if (t.closest && (t.closest('.priv-row') || t.closest('.quick-menu'))) return;
      this.menuOpen = false;
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.menuOpen) {
        this.menuOpen = false;
      }
    },
    onChooseCheckin() {
      this.menuOpen = false;
      this.$nextTick(() => { this.checkinConfirmDialog = true; });
    },
    async confirmCheckin() {
      const wasCheckedIn = this.isCheckedIn;
      this.checkinConfirmDialog = false;
      if (!this.selectedBooking) return;
      this.$emit('onLoading', true);
      try {
        const token = this.$store.getters.getToken;
        const data = await togglePrivateBookingCheckin(this.baseURL, token, this.selectedBooking.privatebookingid, wasCheckedIn);
        if (data.success) {
          await this.getPrivateBookingList();
          this.$emit('onUpdateDataSuccess');
        } else {
          this.$emit('onErrorHandler',
            (data && data.message) ||
            (wasCheckedIn ? this.$t('bookingList.undoCheckinFail') : this.$t('bookingList.checkinFail')));
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          this.$emit('onErrorHandler', error.message);
        }
      } finally {
        this.$emit('onLoading', false);
      }
    },
  },
};
</script>

<style scoped>
/* floor only — never caps growth when there are many bookings — so this card
   doesn't look tiny/lopsided next to the group-class card above when it's empty */
.private-booking-roster { padding: 4px; min-height: 300px; }
.roster-head { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px;
  padding: 4px 4px 14px; }
/* card look matches GroupingSheet.vue's coach-schedule Private section (.gs-pv-item etc.)
   so the two "Private booking session" views read as the same component style — including
   the same max-width, so cards don't stretch edge-to-edge across the wider Dashboard column */
.priv-list { max-width: 560px; }
.priv-row { border: 1px solid var(--c-border); border-radius: 10px; padding: 8px 12px; background: var(--c-surface); }
.priv-row:hover { border-color: var(--c-primary); box-shadow: var(--shadow-sm); }
.priv-line1 { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.priv-time { display: inline-flex; align-items: center; gap: 4px; font-weight: 700; font-size: 13px; color: var(--c-text-heading); }
.priv-time .mdi { color: var(--c-primary); }
.priv-coach { display: flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: var(--c-text-heading); margin-top: 5px; }
.priv-coach .mdi { color: var(--c-primary); }
.priv-money { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: var(--c-text-heading); margin-top: 5px; }
.priv-money .mdi { color: var(--c-success); font-size: 15px; }
.priv-students { display: flex; align-items: center; gap: 5px; font-size: 12.5px; margin-top: 4px; }
.priv-students .mdi { color: var(--c-text-muted); }
.priv-note { font-size: 12px; color: var(--c-text-muted); margin-top: 2px; }
.private-booking-roster :deep(.v-overlay__content) { box-shadow: none; }
</style>
